// useDialog — what a hand-rolled overlay needs to be a real dialog (attendance audit 2026-10-21): focus moves INTO it on
// open, Tab / Shift+Tab wrap inside it, Escape closes it, and focus returns to what opened it. The same contract as
// primitives.jsx Modal (review 2026-09-17), plus Escape, for overlays that keep their own markup.
// Spread `dialogProps` on the dialog box and put `titleId` on its heading.
import { useEffect, useId, useRef } from 'react'

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

export function useDialog(onClose) {
  const ref = useRef(null)
  const titleId = useId()
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const opener = document.activeElement
    const el = ref.current
    if (el && !el.contains(document.activeElement)) {
      const first = el.querySelector('input:not([disabled]),select:not([disabled]),textarea:not([disabled])')
      ;(first || el).focus({ preventScroll: true })
    }
    return () => { if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true }) }
  }, [])
  const onKeyDown = e => {
    if (e.key === 'Escape') { e.stopPropagation(); closeRef.current?.(); return }
    if (e.key !== 'Tab' || !ref.current) return
    const f = [...ref.current.querySelectorAll(FOCUSABLE)].filter(n => n.offsetParent !== null || n === document.activeElement)
    if (!f.length) { e.preventDefault(); return }
    const first = f[0], last = f[f.length - 1]
    if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }
  return { titleId, dialogProps: { ref, role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId, tabIndex: -1, onKeyDown } }
}
